import { useState } from "react";
import { ArrowRight, Instagram } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

const SUBJECTS = ["Customer Support", "Order Questions", "Returns & Exchanges", "General Inquiries"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", order_number: "", subject: SUBJECTS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim() || !form.email.includes("@")) {
      toast.error("FILL IN NAME, VALID EMAIL AND MESSAGE");
      return;
    }
    setLoading(true);
    try {
      await api.contact({ ...form, order_number: form.order_number || null });
      setSent(true);
    } catch {
      toast.error("SOMETHING WENT WRONG. TRY AGAIN.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-wire bg-transparent px-5 py-4 font-mono text-xs tracking-[0.15em] text-white placeholder:text-neutral-600 focus:border-white focus:outline-none";

  return (
    <div data-testid="contact-page">
      <PageHeader overline="GET IN TOUCH" title="Contact" sub="Questions about an order, sizing, returns or the next drop — we read everything." />
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-14">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">QUICK TOPICS</p>
            <div className="mt-5 space-y-2">
              {SUBJECTS.map((s) => (
                <button
                  key={s}
                  data-testid={`contact-topic-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => setForm((f) => ({ ...f, subject: s }))}
                  className={`block w-full border px-5 py-3.5 text-left font-mono text-[11px] tracking-[0.25em] transition-colors ${
                    form.subject === s ? "border-white bg-white text-black" : "border-wire text-neutral-400 hover:border-neutral-500 hover:text-white"
                  }`}
                >
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="mt-10 border border-wire p-6">
              <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">DIRECT</p>
              <p className="mt-4 font-mono text-xs tracking-wider text-neutral-300" data-testid="contact-email">support@dejonrichh.com</p>
              <div className="mt-6 flex gap-3">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram"
                  className="border border-wire p-3 text-neutral-400 transition-colors hover:border-white hover:text-white" aria-label="Instagram">
                  <Instagram size={15} strokeWidth={1.5} />
                </a>
                <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" data-testid="contact-tiktok"
                  className="border border-wire p-3 text-neutral-400 transition-colors hover:border-white hover:text-white" aria-label="TikTok">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            {sent ? (
              <div className="flex h-full flex-col items-center justify-center border border-wire px-8 py-24 text-center" data-testid="contact-success">
                <p className="font-display text-3xl font-extrabold uppercase tracking-tight text-white">Message Sent</p>
                <p className="mt-6 max-w-md font-mono text-xs leading-relaxed tracking-[0.2em] text-neutral-400">
                  THANK YOU FOR CONTACTING DEJON RICHH. WE'LL GET BACK TO YOU SOON.
                </p>
                <button data-testid="contact-send-another" onClick={() => { setSent(false); setForm({ name: "", email: "", order_number: "", subject: SUBJECTS[0], message: "" }); }}
                  className="mt-10 border border-wire px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-neutral-300 transition-colors hover:border-white hover:text-white">
                  SEND ANOTHER
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-wire p-6 sm:p-10" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-neutral-500">NAME *</label>
                    <input data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="YOUR NAME" className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-neutral-500">EMAIL *</label>
                    <input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="EMAIL ADDRESS" className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-neutral-500">ORDER NUMBER (OPTIONAL)</label>
                    <input data-testid="contact-order-input" value={form.order_number} onChange={set("order_number")} placeholder="DR-XXXXXX" className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-neutral-500">SUBJECT *</label>
                    <select data-testid="contact-subject-select" value={form.subject} onChange={set("subject")}
                      className="w-full border border-wire bg-obsidian px-5 py-4 font-mono text-xs tracking-[0.15em] text-white focus:border-white focus:outline-none">
                      {SUBJECTS.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-neutral-500">MESSAGE *</label>
                  <textarea data-testid="contact-message-input" value={form.message} onChange={set("message")} rows={6} placeholder="HOW CAN WE HELP?" className={`${inputCls} resize-none`} />
                </div>
                <button data-testid="contact-send-button" disabled={loading}
                  className="group mt-8 flex w-full items-center justify-center gap-3 bg-white py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60 sm:w-auto sm:px-14">
                  {loading ? "SENDING..." : <>SEND MESSAGE <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
